// ─── App-wide UI settings persisted as one localStorage entry ─────────────────
// Key: 'gateway-settings'
// All UI preferences (theme, colors, sidebar) live here so logout only needs
// to preserve / restore this single key.
import { APP_HEX } from '@/constant/theme/hex.constant';
export const SETTINGS_KEY = 'gateway-settings';

export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  sidebarLocked: boolean; // whether the sidebar is pinned open (expand-lock enabled)
  sidebarOpen: boolean; // open/collapsed state when locked
}

export const SETTINGS_DEFAULTS: AppSettings = {
  theme: 'light',
  colors: { primary: APP_HEX.primary, secondary: APP_HEX.secondary, tertiary: APP_HEX.tertiary },
  sidebarLocked: false,
  sidebarOpen: true,
};

export const getSettings = (): AppSettings => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...SETTINGS_DEFAULTS,
        ...parsed,
        colors: { ...SETTINGS_DEFAULTS.colors, ...parsed.colors },
      };
    }
    // Migrate from old separate keys if they exist
    const oldTheme = localStorage.getItem('gateway-theme');
    const oldColors = localStorage.getItem('gateway-colors');
    const migrated: AppSettings = { ...SETTINGS_DEFAULTS };
    if (oldTheme === 'light' || oldTheme === 'dark' || oldTheme === 'system')
      migrated.theme = oldTheme;
    if (oldColors)
      try {
        migrated.colors = {
          ...SETTINGS_DEFAULTS.colors,
          ...JSON.parse(oldColors),
        };
      } catch {
        /* ignore */
      }
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(migrated));
    // Remove old keys after migration
    localStorage.removeItem('gateway-theme');
    localStorage.removeItem('gateway-colors');
    return migrated;
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
};

export const updateSettings = (patch: Partial<AppSettings>) => {
  const current = getSettings();
  const next = { ...current, ...patch };
  // Deep merge colors if provided
  if (patch.colors) next.colors = { ...current.colors, ...patch.colors };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  return next;
};
